import {
  type BillingInterval,
  BillingIntervalSelector,
  buildFeatureCategoryMap,
  type DiscountDisplayData,
  EnterpriseCard,
  type FeatureDisplayData,
  getActivePromos,
  getAvailableIntervals,
  getDiscountForInterval,
  type PlanDisplayData,
  PricingCard,
  type PricingTier,
} from '@xhubio-saas/core-frontend/plugin-subscription'
import { PackageOpen, Zap } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export interface PlansSectionProps {
  tiers: PricingTier[]
  pricingPlans: PlanDisplayData[]
  discounts: DiscountDisplayData[]
  featureDisplayData: FeatureDisplayData[]
  currentPlanId: string | null
  currentPlanName: string | null
  /** Checkout or — for the free plan — downgrade. Same handler as core `SubscriptionPage`. */
  onSelect: (planId: string, interval: BillingInterval) => void
  /** The plan whose checkout is running. */
  loadingPlanId: string | null
}

/**
 * The „Plans" tab — the pricing cards of core-frontend from `getPublishedPlanDisplay`, unchanged: interval selector,
 * promo line, main tiers in a grid, enterprise card below. Same split and the same default interval as
 * `SubscriptionPage`, so both read alike.
 *
 * ⚪ Translates in the `subscription` namespace (interval names, empty state) — the cards themselves read it too, so
 * the plugin keeps registering it even without its route.
 */
export function PlansSection({
  tiers,
  pricingPlans,
  discounts,
  featureDisplayData,
  currentPlanId,
  currentPlanName,
  onSelect,
  loadingPlanId,
}: PlansSectionProps) {
  const { t } = useTranslation('subscription')

  const intervals = useMemo(
    () =>
      getAvailableIntervals(pricingPlans, discounts, {
        daily: t('billing.interval.daily'),
        weekly: t('billing.interval.weekly'),
        monthly: t('billing.interval.monthly'),
        quarterly: t('billing.interval.quarterly'),
        yearly: t('billing.interval.yearly'),
      }),
    [pricingPlans, discounts, t]
  )

  // Default: the interval with the highest permanent discount, else yearly, else the first (SubscriptionPage).
  const defaultInterval = useMemo<BillingInterval>(() => {
    const withDiscount = intervals.filter((i) => i.discount)
    if (withDiscount.length > 0) {
      return withDiscount.reduce((best, cur) =>
        (cur.discount?.value ?? 0) > (best.discount?.value ?? 0) ? cur : best
      ).interval
    }
    return (
      intervals.find((i) => i.interval === 'yearly')?.interval ??
      intervals[0]?.interval ??
      'monthly'
    )
  }, [intervals])
  const [chosen, setChosen] = useState<BillingInterval | null>(null)
  const interval = chosen ?? defaultInterval

  const featureCategoryMap = useMemo(
    () => buildFeatureCategoryMap(featureDisplayData),
    [featureDisplayData]
  )
  const promos = getActivePromos(discounts, interval)
  const intervalDiscount = getDiscountForInterval(discounts, interval)

  const isCustom = (tier: PricingTier) =>
    typeof tier.prices.monthly === 'string' || typeof tier.price === 'string'
  const mainTiers = tiers.filter((tier) => !isCustom(tier))
  const enterpriseTier = tiers.find(isCustom)
  const isCurrent = (tier: PricingTier) =>
    tier.id === currentPlanId || (currentPlanName !== null && tier.name === currentPlanName)

  if (tiers.length === 0) {
    return (
      <div
        className="flex flex-col items-center gap-2 rounded-lg border py-10 text-center"
        data-testid="billing-plans-empty"
      >
        <PackageOpen className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
        <p className="font-medium">{t('noPlans.title')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid="billing-plans">
      {/* The selector is a pill row (`px-5`); `overflow-x-auto` on its own row keeps a third interval from
          widening the page at 320 px instead of squeezing the labels onto two lines. */}
      <div className="flex justify-center overflow-x-auto">
        <BillingIntervalSelector intervals={intervals} value={interval} onChange={setChosen} />
      </div>

      {promos.length > 0 && (
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
            <Zap className="h-4 w-4 fill-current" aria-hidden="true" />
            <span>{promos[0]?.description}</span>
          </div>
        </div>
      )}

      {/* Seven aligned rows per card (PricingCard is built on subgrid) — the grid template comes from
          SubscriptionPage. */}
      <div
        className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-6"
        style={{ gridTemplateRows: 'repeat(7, auto)' }}
        data-testid="pricing-cards-grid"
      >
        {mainTiers.map((tier) => (
          <PricingCard
            key={tier.id}
            tier={tier}
            billingInterval={interval}
            isCurrentPlan={isCurrent(tier)}
            onSelect={onSelect}
            isLoading={loadingPlanId === tier.id}
            promoDiscounts={promos}
            intervalDiscount={intervalDiscount}
            featureCategoryMap={featureCategoryMap}
          />
        ))}
      </div>

      {enterpriseTier && (
        <EnterpriseCard
          tier={enterpriseTier}
          billingInterval={interval}
          isCurrentPlan={isCurrent(enterpriseTier)}
          onSelect={onSelect}
          isLoading={loadingPlanId === enterpriseTier.id}
          featureCategoryMap={featureCategoryMap}
        />
      )}
    </div>
  )
}
